import { CSSProperties, useId, useState } from 'react';
import { CalendarSheet } from './CalendarSheet';
import { RangeCalendar } from './RangeCalendar';
import { dateRange, shortDate } from '../../../lib/almaFlow/dates';
import { isRangeComplete, rangeLength } from '../../../lib/almaFlow/calendar';
import { lightTap } from '../../../lib/haptics';

/**
 * DateRangeRow — one stay on a hairline row: the city on the left, its dates on
 * the right ("Oct 14 – 20"). Tapping it opens a calendar sheet where the first
 * day and the last day are two taps in one view. The range stays marked, and one
 * button below says what to pick next ("Pick your last day"), then confirms
 * ("Confirm", or "Next: Porto" when another city still needs dates); the sheet
 * retracts when it's pressed.
 *
 * Open it from outside with `open` / `onOpenChange` (to move on to the next
 * city after `onConfirm`); leave both off and the row manages itself. A `locked`
 * row can't be opened yet, and says why in `lockedHint`.
 *
 * `error` is a plain-words message under the row, e.g. "Pick a date after Oct 14."
 */
export interface DateRangeRowProps {
  /** The city, or "Dates" for a single stay. */
  label: string;
  /** ISO dates, '' when not picked yet. */
  from: string;
  to: string;
  /** Earliest pickable ISO date. */
  min: string;
  /** Today's ISO date, marked in the calendar. */
  today?: string;
  onChange: (from: string, to: string) => void;
  /** Controlled open state; leave off to let the row manage it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The city that comes next and has no dates yet: the confirm button says "Next: Porto" and `onConfirm` can open it. */
  nextLabel?: string;
  /** The traveler confirmed the dates; the sheet is retracting. */
  onConfirm?: () => void;
  /** Can't be opened yet (the city before it has no dates). */
  locked?: boolean;
  lockedHint?: string;
  /** Shown in place of the dates until they're picked. Default "Pick dates". */
  placeholder?: string;
  error?: string;
  /** Drops the top hairline (rows stacked under another row). */
  continued?: boolean;
  style?: CSSProperties;
}

export function DateRangeRow({
  label, from, to, min, today, onChange, open: openProp, onOpenChange, nextLabel, onConfirm, locked = false, lockedHint, placeholder = 'Pick dates', error, continued = false, style,
}: DateRangeRowProps) {
  const errorId = useId();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (value: boolean) => { setOpenState(value); onOpenChange?.(value); };
  const complete = isRangeComplete({ from, to });
  const days = complete ? rangeLength({ from, to }) : 0;
  const dayCount = days === 1 ? '1 day' : `${days} days`;
  // The button is the guide: what to pick next, then what confirming does.
  const confirmLabel = !from ? 'Pick your first day' : !complete ? 'Pick your last day' : nextLabel ? `Next: ${nextLabel}` : 'Confirm';
  // A one-day stay is just that day; a range is "Oct 14 – 20"; with only a first day picked, "Oct 14 – …".
  const shown = complete ? (from === to ? shortDate(from) : dateRange(from, to)) : from ? `${shortDate(from)} – …` : placeholder;

  return (
    <div style={style}>
      <button
        type="button"
        onClick={() => { if (!locked) setOpen(true); }}
        disabled={locked}
        aria-haspopup="dialog"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className="sk-flow-focus"
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12, minHeight: 52, padding: 0, background: 'transparent',
          border: 'none', borderTop: continued ? 'none' : '1px solid var(--line)', borderBottom: '1px solid var(--line)',
          fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', textAlign: 'left', cursor: locked ? 'default' : 'pointer',
          opacity: locked ? 0.5 : 1,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, color: 'var(--chip-text)' }}>
          {label}
          {locked && lockedHint ? <span style={{ display: 'block', fontSize: 'var(--fs-caption)', color: 'var(--text-dim)' }}>{lockedHint}</span> : null}
        </span>
        <span style={{ color: complete || from ? 'var(--text)' : 'var(--chip-text)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
          {shown}
          {days > 0 ? <span style={{ display: 'block', fontSize: 'var(--fs-caption)', color: 'var(--text-dim)' }}>{dayCount}</span> : null}
        </span>
      </button>
      {error ? (
        <p id={errorId} aria-live="polite" style={{ margin: '6px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption)', color: 'var(--text)' }}>{error}</p>
      ) : null}

      <CalendarSheet
        open={open}
        onClose={() => setOpen(false)}
        title={label}
        // The dates read first; how many days that is stays a quiet aside.
        subtitle={complete ? (
          <>
            {from === to ? shortDate(from) : dateRange(from, to)}
            <span style={{ color: 'var(--text-dim)' }}> · {dayCount}</span>
          </>
        ) : from ? `${shortDate(from)} – …` : undefined}
        status={confirmLabel}
        footer={(
          <button
            type="button"
            onClick={() => { lightTap(); setOpen(false); onConfirm?.(); }}
            disabled={!complete}
            className="sk-flow-focus"
            style={{
              flex: 'none', height: 44, borderRadius: 'var(--r-pill)', border: 'none', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-subhead)', fontWeight: 600,
              cursor: complete ? 'pointer' : 'not-allowed',
              // Disabled fades by swapping to the chip tokens, never opacity (same as the question sheet's Continue).
              ...(complete ? { background: 'var(--text)', color: 'var(--bg)' } : { background: 'var(--chip-bg)', color: 'var(--chip-text)' }),
            }}
          >
            {confirmLabel}
          </button>
        )}
      >
        <RangeCalendar from={from} to={to} min={min} today={today} onChange={onChange} />
      </CalendarSheet>
    </div>
  );
}

export default DateRangeRow;
